import { Component, Input, ViewChild } from '@angular/core';
import { BonAchatTpvDto, BonsAchatsTpvLexiClient, DeviseDto, DevisesLexiClient, StatutBonAchat, TypeBon, Permissions, BonAchatCsvImportResultDto } from '@lexi-clients/lexi';
import CustomStore from 'devextreme/data/custom_store';
import { AuthService } from 'lexi-angular/src/app/settings/auth.service';
import { BonAchatListService } from '../../../services/bon-achat.service';
import { CustomStoreService } from 'lexi-angular/src/app/services/custom-store.service';
import { lastValueFrom } from 'rxjs';
import { NotificationType } from '../references/references';
import notify from 'devextreme/ui/notify';
import { DxDataGridComponent } from 'devextreme-angular';
import { ExportingEvent } from 'devextreme/ui/data_grid';
import { ExportDataGridService } from 'lexi-angular/src/app/shared/services/export-data-grid.service';
import { DownloadService } from 'lexi-angular/src/app/services/download.service';

@Component({
  selector: 'app-bon-achat-datagrid',
  templateUrl: './bon-achat-datagrid.component.html',
  styleUrls: ['./bon-achat-datagrid.component.scss']
})
export class BonAchatDatagridComponent {
  @ViewChild('datagrid') datagrid: DxDataGridComponent;

  @Input() articleId: number;
  @Input() title: string;

  bonsAchatDataSource: CustomStore;
  deviseDataSource: DeviseDto[] = [];
  statut = StatutBonAchat;
  type = TypeBon;
  dateFin: Date = null;
  dateDebut: Date = null;
  filterValue = null;

  onlyValid: boolean = false;
  canAdministrerBonAchat: boolean = false;

  statutDataSource = [
    {id: this.statut.annule, libelle: "Annulé"},
    {id: this.statut.perime, libelle: "Périmé"},
    {id: this.statut.utilise, libelle: "Utilisé"},
    {id: this.statut.valide, libelle: "Validé"}
  ]

  typeDataSource = [
    {id: this.type.achat, libelle: "Achat"},
    {id: this.type.fidelite, libelle: "Fidélité"}
  ]

  fichierCsv: File[] = [];
  importCsvResultMsg: string;
  importCsvResultMsg2: string;

  showImportCsvPopup: boolean = false;
  showLoader: boolean = false;
  importHasFailed: boolean = false;
  recuperationTemplateEncours = false;

  constructor(
    private readonly bonsAchatsTpvLexiClient: BonsAchatsTpvLexiClient,
    private readonly devisesLexiClient: DevisesLexiClient,
    private readonly bonAchatListService: BonAchatListService,
    private readonly authService: AuthService,
    private readonly exportDataGridService: ExportDataGridService,
    private readonly downloadService: DownloadService
  ) { }

  async ngOnInit() {
    this.canAdministrerBonAchat = this.authService.securityUserisGrantedWith(Permissions.administrationBonsAchat);

    this.setBonsAchat();
    await this.setDevise();
  }

  setBonsAchat = () => {
    const params = new Map().set("onlyValid", this.onlyValid);
    if (this.dateDebut != null) params.set("debut", this.dateDebut.toISOString());
    if (this.dateFin != null) params.set("fin", this.dateFin.toISOString());

    this.initFilters();
    this.bonsAchatDataSource = new CustomStoreService(this.bonAchatListService).getCustomStore(params);
  }

  private initFilters() {
    const filters = [];
    if (this.articleId) {
      filters.push(['articleId', '=', this.articleId]);
    }

    if (filters.length) {
      this.filterValue = filters;
    }
  }

  async setDevise() {
    this.deviseDataSource = await lastValueFrom(this.devisesLexiClient.getAll());
  }

  async onRowInserting(e) {
    const bonAchat: BonAchatTpvDto = {
      deviseId: e.deviseId,
      numero: e.numero,
      type: e.type,
      statut: e.statut,
      dateValidite: e.dateValidite,
      articleId: this.articleId ?? null
    };
    await lastValueFrom(this.bonsAchatsTpvLexiClient.create(bonAchat));
    this.setBonsAchat();
    notify({closeOnClick: true, message: "Insertion effectuée avec succès"}, NotificationType.Success);
  }

  async onRowUpdating(e) {
    const bonAchat: BonAchatTpvDto = { ...e.oldData, ...e.newData };
    await lastValueFrom(this.bonsAchatsTpvLexiClient.update(bonAchat));
    this.setBonsAchat();
    notify({closeOnClick: true, message: "Modifications effectuées avec succès"}, NotificationType.Success);
  }

  async onRowRemoving(e) {
    await lastValueFrom(this.bonsAchatsTpvLexiClient._delete(e.id));
    this.setBonsAchat();
    notify({closeOnClick: true, message: "Suppression effectuée avec succès"}, NotificationType.Success);
  }

  onExporting(e: ExportingEvent, filename: string) {
    this.exportDataGridService.onExporting(e, filename);
  }

  //#region Import CSV
  cancelImportCsv() {
    this.showImportCsvPopup = false;
    this.resetImportResultMsg();
    this.fichierCsv = [];
  }

  resetImportResultMsg() {
    this.importCsvResultMsg = null;
    this.importCsvResultMsg2 = null;
  }

  async importDataFromCsv() {
    this.resetImportResultMsg();
    this.showLoader = true;
    try {
      const response: BonAchatCsvImportResultDto = await lastValueFrom(this.bonsAchatsTpvLexiClient.importFromCsv(true, this.fichierCsv[0]));

      // Erreur
      if (response.hasFailed) {
        this.importHasFailed = true;
        if (response.errorMessage != null) {
          this.importCsvResultMsg = response.errorMessage;
        }

        else {
          if (response.hasWrongHeadersCount) {
            this.importCsvResultMsg = "Le nombre de colonnes est incorrecte, il y a plus de colonnes que celles attendues.";
          }

          else {
            this.importCsvResultMsg = `Les colonnes suivantes sont invalides: ${response.invalidHeaders.join(', ')}.`;
          }

          if (response.expectedHeaders != null) {
            this.importCsvResultMsg2 = `Colonnes attendues: (${response.expectedHeaders.join(', ')})`;
          }
        }
      }

      else if (response.bonsAchatOnError.length > 0) {
        let errorMessage = "";
        response.bonsAchatOnError.forEach(x => errorMessage += `${x}\n`);
        this.importCsvResultMsg = `Certains comptes généraux n'ont pas pu être importés. Le détail a été copié dans le presse papier`;
        this.importCsvResultMsg2 = "Importation incomplète";
        navigator.clipboard.writeText(errorMessage).then().catch(e => console.error(e));
        this.setBonsAchat();
      }

      // Succès
      else {
        this.importHasFailed = false;
        this.setBonsAchat();
        const sCreated = response.nbBonsAchatCreated > 1 ? "s" : "";
        const sUpdated = response.nbBonsAchatUpdated > 1 ? "s" : "";
        this.importCsvResultMsg = `${response.nbBonsAchatCreated} compte${sCreated} créé${sCreated},  ${response.nbBonsAchatUpdated} compte${sUpdated} mis à jour. \nL'importation s'est terminée.`;
      }

      this.fichierCsv = [];
    }
    finally {
      this.showLoader = false;
    }
  }

  async downloadTemplate() {
    try {
      this.recuperationTemplateEncours = true;
      const response = await lastValueFrom(this.bonsAchatsTpvLexiClient.getTemplateImportCsv('response'));
      this.downloadService.directDownloadFile(response, 'csv');
    }
    finally {
      this.recuperationTemplateEncours = false;
    }
  }
  //#endregion
}
